<template>
  <v-dialog
    :value="value"
    :fullscreen="$vuetify.breakpoint.smAndDown"
    @input="$emit('input', $event)"
    max-width="700"
    persistent
    scrollable
    content-class="job-application-dialog">
    <v-card dark class="dialog-card">
      <v-card-title class="dialog-header">
        <div class="dialog-header__copy">
          <span class="dialog-header__icon" aria-hidden="true">
            <v-icon>mdi-share-outline</v-icon>
          </span>
          <div>
            <div class="dialog-eyebrow">Zuständigkeit</div>
            <h2>Bewerbung weiterleiten</h2>
            <p>Neue verantwortliche Personen auswählen und informieren.</p>
          </div>
        </div>
        <v-btn icon class="dialog-close" @click="closeDialog" :disabled="isSubmitting" title="Dialog schliessen" aria-label="Dialog schliessen">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider class="dialog-divider"></v-divider>

      <v-card-text class="dialog-content">
        <v-alert type="info" color="#009640" text class="forward-notice">
          Die aktuell zuständigen Personen verlieren den Zugriff. Die neuen Personen erhalten eine E-Mail und Push-Benachrichtigung.
        </v-alert>

        <section class="dialog-section">
          <div class="section-heading">
            <span class="section-heading__icon"><v-icon>mdi-account-plus-outline</v-icon></span>
            <div>
              <h3>Zuständige Person hinzufügen</h3>
              <p>Es werden nur Mitarbeitende mit der benötigten Berechtigung angezeigt.</p>
            </div>
          </div>
          <v-autocomplete
            v-model="userToAdd"
            :items="availableUsers"
            :loading="isLoadingUsers"
            :item-text="userDisplayName"
            item-value="_id"
            return-object
            label="Mitarbeiter suchen..."
            placeholder="Name oder E-Mail eingeben"
            outlined
            dense
            hide-details
            clearable
            no-data-text="Keine berechtigten Mitarbeiter gefunden"
            class="user-search"
            @change="addUserToList">
            <template v-slot:item="{ item }">
              <v-list-item-avatar size="36" class="user-avatar">
                <span> {{ (item.vorname || "U").charAt(0) }}{{ (item.nachname || "").charAt(0) }} </span>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ item.vorname }} {{ item.nachname }}</v-list-item-title>
                <v-list-item-subtitle class="caption">{{ item.email }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </section>

        <section class="dialog-section">
          <div class="section-heading section-heading--with-count">
            <span class="section-heading__icon"><v-icon>mdi-account-multiple-check-outline</v-icon></span>
            <div>
              <h3>Zukünftig zuständig</h3>
              <p>Diese Personen erhalten Zugriff und werden benachrichtigt.</p>
            </div>
            <span class="selection-count">{{ selectedUsers.length }}</span>
          </div>

          <v-list v-if="selectedUsers.length" dense color="transparent" class="selected-user-list">
            <v-list-item v-for="(user, index) in selectedUsers" :key="user._id" class="selected-user">
              <v-list-item-avatar size="36" class="user-avatar">
                <span> {{ (user.vorname || "U").charAt(0) }}{{ (user.nachname || "").charAt(0) }} </span>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ user.vorname }} {{ user.nachname }}</v-list-item-title>
                <v-list-item-subtitle class="caption">{{ user.email }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn
                  icon
                  class="remove-user-action"
                  @click="removeUserFromList(index)"
                  :title="`${user.vorname} ${user.nachname} entfernen`"
                  :aria-label="`${user.vorname} ${user.nachname} entfernen`">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <v-alert v-else type="warning" text class="selection-warning"> Bitte wähle mindestens eine Person aus. </v-alert>
        </section>

        <section class="dialog-section">
          <div class="section-heading">
            <span class="section-heading__icon"><v-icon>mdi-message-text-outline</v-icon></span>
            <div>
              <h3>Nachricht</h3>
              <p>Eine optionale Begründung für die Weiterleitung ergänzen.</p>
            </div>
          </div>
          <v-textarea
            v-model="forwardReason"
            label="Begründung / Nachricht (optional)"
            placeholder="Warum wird diese Bewerbung weitergeleitet?"
            outlined
            dense
            rows="3"
            hide-details></v-textarea>
        </section>
      </v-card-text>

      <v-divider class="dialog-divider"></v-divider>

      <v-card-actions class="dialog-footer">
        <v-spacer></v-spacer>
        <v-btn text class="secondary-action" @click="closeDialog" :disabled="isSubmitting">Abbrechen</v-btn>
        <v-btn color="primary" depressed class="primary-action" @click="submitForward" :loading="isSubmitting" :disabled="selectedUsers.length === 0">
          <v-icon left small>mdi-send-outline</v-icon>
          Weiterleiten
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "@/api"

export default {
  name: "JobApplicationForwardDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    application: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isSubmitting: false,
      isLoadingUsers: false,
      availableUsers: [],
      userToAdd: null,
      selectedUsers: [],
      forwardReason: "",
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) {
          this.resetState()
          this.loadEligibleUsers()
        }
      },
    },
  },
  methods: {
    resetState() {
      this.userToAdd = null
      this.selectedUsers = []
      this.forwardReason = ""
    },
    async loadEligibleUsers() {
      this.isLoadingUsers = true
      try {
        this.availableUsers = await api.jobApplications.fetchEligibleUsers()
      } catch (error) {
        console.error("Fehler beim Laden der berechtigten User:", error)
        if (this.$toast) this.$toast.error("Berechtigte Mitarbeiter konnten nicht geladen werden")
      } finally {
        this.isLoadingUsers = false
      }
    },
    userDisplayName(user) {
      if (!user) return ""
      return `${user.vorname || ""} ${user.nachname || ""} (${user.email || ""})`.trim()
    },
    addUserToList(user) {
      if (!user) return

      const alreadyAdded = this.selectedUsers.some((u) => u._id === user._id)
      if (alreadyAdded) {
        if (this.$toast) this.$toast.info("Person ist bereits in der Liste")
      } else {
        this.selectedUsers.push(user)
      }

      // Autocomplete leeren auf nextTick, damit der @change-Handler sauber durchläuft
      this.$nextTick(() => {
        this.userToAdd = null
      })
    },
    removeUserFromList(index) {
      this.selectedUsers.splice(index, 1)
    },
    closeDialog() {
      this.$emit("input", false)
    },
    async submitForward() {
      if (this.selectedUsers.length === 0) return

      this.isSubmitting = true
      try {
        const userIds = this.selectedUsers.map((user) => user._id)
        const updatedApplication = await api.jobApplications.forward(this.application._id, userIds, this.forwardReason)

        if (this.$toast) this.$toast.success("Bewerbung erfolgreich weitergeleitet")
        this.$emit("forwarded", updatedApplication)
      } catch (error) {
        console.error("Fehler beim Weiterleiten:", error)
        if (this.$toast) this.$toast.error("Bewerbung konnte nicht weitergeleitet werden")
      } finally {
        this.isSubmitting = false
      }
    },
  },
}
</script>

<style scoped>
.dialog-card {
  --dialog-primary: #009640;
  --dialog-primary-hover: #00a848;
  --dialog-border: rgba(255, 255, 255, 0.1);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  border-radius: 24px !important;
  color: #f4f4f5;
  background: radial-gradient(circle at 92% -8%, rgba(0, 150, 64, 0.09), transparent 22rem), #202021 !important;
  box-shadow: 0 30px 90px rgba(0, 0, 0, 0.58) !important;
}

.dialog-header {
  display: flex;
  flex: 0 0 auto;
  min-height: 112px;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  padding: 24px 26px !important;
  background: rgba(0, 0, 0, 0.2);
}

.dialog-header__copy {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 15px;
}

.dialog-header__icon,
.section-heading__icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(0, 150, 64, 0.3);
  color: #d8f3e2;
  background: rgba(0, 150, 64, 0.14);
}

.dialog-header__icon {
  width: 48px;
  height: 48px;
  border-radius: 15px;
}

.dialog-header__icon .v-icon,
.section-heading__icon .v-icon {
  color: inherit;
}

.dialog-eyebrow {
  margin-bottom: 4px;
  color: #65ca8b;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.dialog-header h2 {
  margin: 0;
  color: #ffffff;
  font-size: 23px;
  font-weight: 750;
  letter-spacing: -0.025em;
  line-height: 1.2;
}

.dialog-header p {
  margin: 5px 0 0;
  color: #9d9da2;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.45;
}

.dialog-close,
.remove-user-action {
  flex: 0 0 auto;
  width: 44px !important;
  min-width: 44px !important;
  height: 44px !important;
  border-radius: 13px;
}

.dialog-close {
  color: #b8b8bd !important;
}

.dialog-close:hover,
.dialog-close:focus-visible {
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.07);
}

.dialog-divider {
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.dialog-content {
  display: grid;
  max-height: min(72vh, 780px);
  gap: 14px;
  padding: 20px 24px 26px !important;
}

.forward-notice,
.selection-warning {
  margin: 0 !important;
  border: 1px solid currentColor;
  border-radius: 15px;
  font-size: 12px;
  line-height: 1.5;
}

.forward-notice {
  background: rgba(0, 150, 64, 0.07) !important;
}

.dialog-section {
  padding: 19px;
  border: 1px solid var(--dialog-border);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.03);
}

.section-heading {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  gap: 11px;
  margin-bottom: 16px;
}

.section-heading--with-count {
  align-items: center;
}

.section-heading__icon {
  width: 34px;
  height: 34px;
  border-radius: 11px;
}

.section-heading__icon .v-icon {
  font-size: 18px;
}

.section-heading > div {
  min-width: 0;
}

.section-heading h3 {
  margin: 1px 0 0;
  color: #eeeeef;
  font-size: 15px;
  font-weight: 720;
  line-height: 1.3;
}

.section-heading p {
  margin: 3px 0 0;
  color: #8f8f94;
  font-size: 12px;
  line-height: 1.4;
}

.selection-count {
  display: inline-flex;
  flex: 0 0 auto;
  min-width: 30px;
  height: 30px;
  margin-left: auto;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(0, 150, 64, 0.28);
  border-radius: 999px;
  color: #ccebd8;
  background: rgba(0, 150, 64, 0.11);
  font-size: 12px;
  font-weight: 800;
}

.dialog-card ::v-deep .v-input__slot {
  border-radius: 13px !important;
  background: rgba(0, 0, 0, 0.14) !important;
}

.dialog-card ::v-deep .v-text-field--outlined fieldset {
  border-color: rgba(255, 255, 255, 0.14);
  transition: border-color 160ms ease, box-shadow 160ms ease;
}

.dialog-card ::v-deep .v-input--is-focused fieldset {
  border-width: 1px;
  border-color: var(--dialog-primary) !important;
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.12);
}

.dialog-card ::v-deep .v-label {
  color: #9d9da2;
}

.dialog-card ::v-deep .v-progress-linear.primary--text {
  color: var(--dialog-primary) !important;
}

.user-avatar {
  border: 1px solid rgba(0, 150, 64, 0.25);
  color: #d8f3e2 !important;
  background: rgba(0, 150, 64, 0.13) !important;
}

.user-avatar span {
  margin: auto;
  color: inherit;
  font-size: 11px;
  font-weight: 800;
}

.selected-user-list {
  display: grid;
  gap: 8px;
  padding: 0 !important;
}

.selected-user {
  min-height: 62px;
  padding: 5px 7px 5px 12px !important;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 14px;
  background: rgba(0, 0, 0, 0.13);
}

.selected-user ::v-deep .v-list-item__title {
  color: #e2e2e4;
  font-size: 13px;
  font-weight: 650;
}

.selected-user ::v-deep .v-list-item__subtitle {
  color: #909095 !important;
}

.remove-user-action {
  color: #ef8585 !important;
}

.remove-user-action:hover,
.remove-user-action:focus-visible {
  color: #ffffff !important;
  background: rgba(220, 58, 70, 0.14);
}

.selection-warning {
  background: rgba(230, 164, 35, 0.07) !important;
}

.dialog-footer {
  flex: 0 0 auto;
  min-height: 76px;
  gap: 8px;
  padding: 14px 20px !important;
  background: rgba(18, 18, 19, 0.92);
  backdrop-filter: blur(16px);
}

.secondary-action,
.primary-action {
  min-height: 44px;
  border-radius: 13px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.secondary-action {
  color: #c3c3c7 !important;
}

.primary-action {
  padding-inline: 18px !important;
  color: #ffffff !important;
  background: var(--dialog-primary) !important;
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.22);
}

.primary-action:hover,
.primary-action:focus-visible {
  background: var(--dialog-primary-hover) !important;
}

.primary-action.v-btn--disabled {
  color: rgba(255, 255, 255, 0.34) !important;
  background: rgba(255, 255, 255, 0.075) !important;
  box-shadow: none;
}

.dialog-card ::v-deep .v-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.25) !important;
}

@media (max-width: 959px) {
  .dialog-card {
    border: 0 !important;
    border-radius: 0 !important;
  }

  .dialog-content {
    max-height: none;
  }
}

@media (max-width: 599px) {
  .dialog-card {
    border: 0 !important;
    border-radius: 0 !important;
  }

  .dialog-header {
    min-height: auto;
    padding: 18px 16px !important;
  }

  .dialog-header__copy {
    align-items: flex-start;
    gap: 11px;
  }

  .dialog-header__icon {
    width: 42px;
    height: 42px;
    border-radius: 13px;
  }

  .dialog-header h2 {
    font-size: 19px;
  }

  .dialog-header p {
    display: none;
  }

  .dialog-content {
    max-height: none;
    gap: 12px;
    padding: 14px !important;
  }

  .dialog-section {
    padding: 15px;
    border-radius: 16px;
  }

  .section-heading p {
    font-size: 11px;
  }

  .dialog-footer {
    min-height: auto;
    padding: 11px 12px !important;
  }

  .dialog-footer .v-spacer {
    display: none;
  }

  .secondary-action,
  .primary-action {
    flex: 1 1 0;
  }
}
</style>
